import { FC, ReactElement, useState } from 'react';
import AppModal from '../app-modal/AppModal';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import i18next from 'i18next';
import { useAuthFetchFromRestAPI } from '../../util/api';

type PatientDeleteModalProps = {
  show: boolean;
  patientId: string;
  onClose: () => void;
  onSave: () => void;
}

const PatientDeleteModal: FC<PatientDeleteModalProps> = ({
  show,
  patientId,
  onClose,
  onSave
}: PatientDeleteModalProps): ReactElement => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { authFetch } = useAuthFetchFromRestAPI();
    
  const handleDelete = async (): Promise<void> => {
    setErrorMessage(null);
    setIsDeleting(true);
      
    try {
      await authFetch(`/api/v1/admin/patient/${patientId}`, { method: 'DELETE' });
      onSave();
    } catch (error: any) {
      console.error(error);
      const msg: string = error?.message ?? (typeof error === 'string' ? error : i18next.t('generic.unknownError'));
      setErrorMessage(msg);
    } finally {
      setIsDeleting(false);
    }
  };
    
    
  return (
    <AppModal show={show} onHide={onClose} backdrop="static" keyboard>
      <Modal.Header closeButton>
        <Modal.Title>{i18next.t('patientDeleteModal.title')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {errorMessage && (
          <Alert variant="danger" className="mb-3">{errorMessage}</Alert>
        )}
        
        <p className="mb-0">
          {i18next.t('patientDeleteModal.confirmText')}
        </p>
        
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          type="button"
          onClick={onClose}
          disabled={isDeleting}
        >
          {i18next.t('generic.cancel')}
        </Button>
        <Button
          variant="danger"
          type="button"
          onClick={handleDelete}
          disabled={isDeleting}
        >
          {isDeleting && (
            <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
          )}
          
          {i18next.t('patientDeleteModal.delete')}
        </Button>
      </Modal.Footer>
    </AppModal>
  );
};


export default PatientDeleteModal;
